import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  ClipboardList,
  MessageSquare,
  Target,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Quote,
  TrendingUp,
  Award,
  Heart,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from '@inertiajs/react';

const HowItWorks = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 },
  };

  const steps = [
    {
      icon: ClipboardList,
      title: 'Complete Your Profile',
      description:
        'Fill out a comprehensive questionnaire tailored to your role and academic path',
      details: [
        'Answer questions about your background and career goals',
        'Specify your areas of expertise or research interest',
        'Set your availability and mentorship preferences',
      ],
      color: 'bg-blue-500',
    },
    {
      icon: Target,
      title: 'Get Matched',
      description:
        'Our intelligent algorithm pairs mentors with compatible mentees across all fields',
      details: [
        'Based on shared academic interests and career goals',
        'Considers availability, time zones, and communication preferences',
        'Ensures complementary expertise in medical or research fields',
      ],
      color: 'bg-purple-500',
    },
    {
      icon: MessageSquare,
      title: 'Connect & Grow',
      description:
        'Start your mentorship journey with meaningful conversations',
      details: [
        'Schedule regular check-ins',
        'Set and track goals together',
        'Share resources and experiences',
      ],
      color: 'bg-green-500',
    },
  ];

  const benefits = [
    {
      title: 'For Mentees',
      items: [
        'Gain insights from experienced professionals in your field',
        'Navigate medical school, graduate programs, or career transitions',
        'Build valuable connections in healthcare and research',
        'Accelerate your academic and professional growth',
      ],
    },
    {
      title: 'For Mentors',
      items: [
        'Give back to the next generation of students and researchers',
        'Develop leadership and mentoring skills',
        'Stay connected with emerging talent across disciplines',
        'Expand your professional network in academia and industry',
      ],
    },
  ];

  // Animated counter hook
  const useCountUp = (end: number, duration: number = 2000) => {
    const [count, setCount] = useState(0);
    const [hasStarted, setHasStarted] = useState(false);

    useEffect(() => {
      if (!hasStarted) return;

      let startTime: number;
      const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };
      requestAnimationFrame(step);
    }, [end, duration, hasStarted]);

    return { count, setHasStarted };
  };

  // Statistics data
  const stats = [
    { number: 2500, suffix: '+', label: 'Active Mentors', icon: Users },
    { number: 5000, suffix: '+', label: 'Students Matched', icon: TrendingUp },
    { number: 95, suffix: '%', label: 'Success Rate', icon: Award },
    { number: 50, suffix: '+', label: 'Fields & Specialties', icon: Heart },
  ];

  // Testimonials data
  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Medical Student → Emergency Medicine Resident',
      quote:
        'My mentor helped me navigate the match process and build confidence for interviews. I matched into my top choice program!',
      avatar: 'SC',
      rating: 5,
    },
    {
      name: 'Dr. Michael Rodriguez',
      role: 'Cardiothoracic Surgeon & Research Director',
      quote:
        'Mentoring students in both clinical and research paths keeps me connected to the next generation. Its incredibly rewarding to give back.',
      avatar: 'MR',
      rating: 5,
    },
    {
      name: 'Alex Thompson',
      role: 'PhD Student in Neuroscience',
      quote:
        'My mentor guided me through choosing research labs and developing my thesis proposal. Their insights were crucial for my success.',
      avatar: 'AT',
      rating: 5,
    },
  ];

  // FAQ data
  const faqs = [
    {
      question: 'How long does the matching process take?',
      answer:
        'Most matches are made within 48-72 hours of completing your profile. Our algorithm works quickly to find compatible mentor-mentee pairs based on your preferences and goals.',
    },
    {
      question: 'Is there a cost to use the platform?',
      answer:
        'No, our platform is completely free for both mentors and mentees. We believe that access to mentorship should not be limited by financial barriers.',
    },
    {
      question: 'What if I dont connect well with my first match?',
      answer:
        'We understand that not every match is perfect. You can request a new match at any time, and our team will work to find someone better suited to your needs and goals.',
    },
    {
      question: 'How much time commitment is expected?',
      answer:
        'The time commitment is flexible and agreed upon by both parties. Most successful mentorship relationships involve 30-60 minutes of conversation every 2-3 weeks.',
    },
    {
      question: 'What specialties and career paths are covered?',
      answer:
        'We cover over 50 medical specialties and research fields, from primary care to specialized areas like neurosurgery, as well as diverse graduate programs in biomedical sciences, public health, and research.',
    },
  ];

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-full bg-gradient-to-b from-background to-secondary/10">
      {/* Hero Section */}
      <section className="relative px-4 py-16 overflow-hidden sm:px-6 lg:px-8 sm:py-20">
        <div className="w-full max-w-6xl mx-auto">
          <motion.div
            className="relative z-10 text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Badge
                className="px-6 py-2 mb-8 text-sm font-medium border border-gray-200 shadow-sm bg-white/80 dark:bg-gray-900/80 dark:border-gray-700 backdrop-blur-sm"
                variant="secondary"
              >
                <Sparkles className="w-4 h-4 mr-2 text-primary" />
                How It Works
              </Badge>
            </motion.div>

            <motion.h1
              className="mb-6 text-4xl font-bold leading-tight tracking-tight sm:text-6xl lg:text-7xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <span className="text-gray-900 dark:text-gray-100">
                Your Journey to{' '}
              </span>
              <motion.span
                className="relative inline-block"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.8 }}
              >
                <span className="relative font-extrabold text-gray-900 dark:text-gray-100">
                  Meaningful
                  {/* Subtle accent behind text */}
                  <span className="absolute inset-0 scale-105 text-primary/20 blur-sm">
                    Meaningful
                  </span>
                </span>
                <br className="block sm:hidden" />
                <span className="relative font-extrabold text-primary">
                  Mentorship
                  {/* Elegant underline */}
                  <svg
                    className="absolute left-0 w-full h-12 -bottom-5"
                    viewBox="0 0 100 15"
                  >
                    <motion.path
                      d="M2,8 C15,5 20,11 35,8 C50,5 55,11 70,8 C85,5 90,11 98,8"
                      stroke="currentColor"
                      strokeWidth="3"
                      fill="none"
                      className="text-primary"
                      strokeLinecap="round"
                      opacity="0.6"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{
                        duration: 1.5,
                        delay: 1.2,
                        ease: 'easeOut',
                      }}
                    />
                    <motion.circle
                      cx="98"
                      cy="8"
                      r="2"
                      fill="currentColor"
                      className="text-primary"
                      initial={{ scale: 0 }}
                      animate={{ scale: [0, 1.5, 1] }}
                      transition={{ duration: 0.5, delay: 2.7 }}
                    />
                  </svg>
                </span>
              </motion.span>
            </motion.h1>

            <motion.p
              className="max-w-3xl mx-auto mb-10 text-lg leading-relaxed sm:text-xl text-muted-foreground"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Whether you're a pre-health or pre-graduate student seeking
              guidance, or a professional ready to share your expertise across
              medical and research fields, our platform makes mentorship
              accessible, impactful, and rewarding.
            </motion.p>

            {/* Elegant inline indicators */}
            <motion.div
              className="flex flex-col items-center justify-center gap-6 mt-12 sm:flex-row"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
            >
              <motion.div
                className="flex items-center gap-4 px-6 py-3 transition-all duration-300 border shadow-sm rounded-xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-gray-200/50 dark:border-gray-700/50 hover:shadow-md"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100">
                    Pre-Health Students
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Medical • Dental • Veterinary
                  </div>
                </div>
              </motion.div>

              <div className="hidden w-12 h-px bg-gray-300 sm:block dark:bg-gray-600"></div>

              <motion.div
                className="flex items-center gap-4 px-6 py-3 transition-all duration-300 border shadow-sm rounded-xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-gray-200/50 dark:border-gray-700/50 hover:shadow-md"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-50 dark:bg-purple-900/30">
                  <Target className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100">
                    Pre-Graduate Students
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    PhD • Masters • Research
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Call-to-action hint */}
            <motion.div
              className="mt-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.5 }}
            >
              <motion.div
                className="inline-flex items-center gap-2 text-muted-foreground"
                animate={{ y: [0, -5, 0] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <span className="text-sm">Scroll to discover how it works</span>
                <ArrowRight className="w-4 h-4 rotate-90" />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        {/* Background Image/Video */}
        <div className="absolute inset-0 -z-20">
          {/* Option 1: Background Image */}
          {/* <div
            className="absolute inset-0 bg-center bg-no-repeat bg-cover opacity-30"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80')`,
            }}
          /> */}

          {/* Option 2: Alternative background - students/mentorship scene */}
          <div
            className="absolute inset-0 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')`,
            }}
          />

          {/* Option 3: Medical/science background */}
          {/* <div 
            className="absolute inset-0 bg-center bg-no-repeat bg-cover opacity-15"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80')`
            }}
          /> */}

          {/* Enhanced gradient overlay with depth */}
          <div className="absolute inset-0">
            {/* Primary gradient for readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/30 to-white/60 dark:from-gray-900/60 dark:via-gray-900/30 dark:to-gray-900/60"></div>

            {/* Radial gradient for spotlight effect */}
            <div className="absolute inset-0 bg-gradient-radial from-transparent via-white/15 to-white/30 dark:from-transparent dark:via-gray-900/15 dark:to-gray-900/30"></div>

            {/* Subtle vignette effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 dark:from-gray-900/20 dark:via-transparent dark:to-gray-900/20"></div>
          </div>
        </div>

        {/* Enhanced meaningful background */}
        <div className="absolute inset-0 overflow-hidden -z-10">
          {/* Main gradient orb */}
          <motion.div
            className="absolute left-[50%] top-0 h-[600px] w-[600px] -translate-x-[50%] rounded-full bg-gradient-to-br from-primary/10 via-purple-500/5 to-blue-500/10 blur-3xl"
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />

          {/* Connection network - representing mentorship connections */}
          <svg
            className="absolute inset-0 w-full h-full opacity-20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="network"
                x="0"
                y="0"
                width="200"
                height="200"
                patternUnits="userSpaceOnUse"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="2"
                  fill="currentColor"
                  className="text-primary"
                >
                  <animate
                    attributeName="r"
                    values="1;3;1"
                    dur="4s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle
                  cx="150"
                  cy="100"
                  r="2"
                  fill="currentColor"
                  className="text-blue-500"
                >
                  <animate
                    attributeName="r"
                    values="1;2.5;1"
                    dur="3s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle
                  cx="100"
                  cy="150"
                  r="2"
                  fill="currentColor"
                  className="text-purple-500"
                >
                  <animate
                    attributeName="r"
                    values="1;2;1"
                    dur="5s"
                    repeatCount="indefinite"
                  />
                </circle>
                <line
                  x1="50"
                  y1="50"
                  x2="150"
                  y2="100"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  className="text-primary/30"
                  opacity="0.5"
                >
                  <animate
                    attributeName="opacity"
                    values="0.3;0.7;0.3"
                    dur="6s"
                    repeatCount="indefinite"
                  />
                </line>
                <line
                  x1="150"
                  y1="100"
                  x2="100"
                  y2="150"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  className="text-blue-500/30"
                  opacity="0.5"
                >
                  <animate
                    attributeName="opacity"
                    values="0.3;0.6;0.3"
                    dur="4s"
                    repeatCount="indefinite"
                  />
                </line>
                <line
                  x1="50"
                  y1="50"
                  x2="100"
                  y2="150"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  className="text-purple-500/30"
                  opacity="0.5"
                >
                  <animate
                    attributeName="opacity"
                    values="0.3;0.8;0.3"
                    dur="7s"
                    repeatCount="indefinite"
                  />
                </line>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#network)" />
          </svg>

          {/* Floating academic icons */}
          <motion.div
            className="absolute left-[15%] top-[20%] opacity-20"
            animate={{
              y: [0, -15, 0],
              rotate: [0, 5, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <svg
              width="60"
              height="60"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 3L1 9L12 15L21 12.09V17H23V9M5 13.18V17.18C5 19.45 8.58 20 12 20S19 19.45 19 17.18V13.18L12 15.45L5 13.18Z"
                fill="currentColor"
                className="text-primary"
              />
            </svg>
          </motion.div>

          <motion.div
            className="absolute right-[20%] top-[25%] opacity-20"
            animate={{
              y: [0, 10, 0],
              rotate: [0, -3, 0],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1,
            }}
          >
            <svg
              width="50"
              height="50"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 13.18V17.18C5 19.45 8.58 21 12 21S19 19.45 19 17.18V13.18L12 15.45L5 13.18ZM12 3L1 9L12 15L21 12.09V17H23V9L12 3Z"
                fill="currentColor"
                className="text-blue-500"
              />
            </svg>
          </motion.div>

          <motion.div
            className="absolute left-[80%] top-[60%] opacity-20"
            animate={{
              y: [0, -8, 0],
              rotate: [0, 8, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2,
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.5 3A6.5 6.5 0 0 1 16 9.5C16 11.11 15.41 12.59 14.44 13.73L14.71 14H15.5L20.5 19L19 20.5L14 15.5V14.71L13.73 14.44C12.59 15.41 11.11 16 9.5 16A6.5 6.5 0 0 1 3 9.5A6.5 6.5 0 0 1 9.5 3M9.5 5C7 5 5 7 5 9.5S7 14 9.5 14S14 12 14 9.5S12 5 9.5 5Z"
                fill="currentColor"
                className="text-purple-500"
              />
            </svg>
          </motion.div>

          <motion.div
            className="absolute left-[10%] top-[70%] opacity-20"
            animate={{
              y: [0, 12, 0],
              rotate: [0, -5, 0],
            }}
            transition={{
              duration: 9,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 3,
            }}
          >
            <svg
              width="45"
              height="45"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L3 7L12 12L21 7L12 2Z"
                fill="currentColor"
                className="text-green-500"
              />
              <path
                d="M3 17L12 22L21 17"
                fill="currentColor"
                className="text-green-500"
                opacity="0.6"
              />
              <path
                d="M3 12L12 17L21 12"
                fill="currentColor"
                className="text-green-500"
                opacity="0.8"
              />
            </svg>
          </motion.div>

          {/* Growth/progress arrows */}
          <motion.div
            className="absolute right-[15%] top-[45%] opacity-30"
            animate={{
              y: [0, -20, 0],
              x: [0, 5, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <svg
              width="80"
              height="30"
              viewBox="0 0 80 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 20 Q 25 5, 40 15 Q 55 25, 70 10"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                className="text-primary/20"
                strokeDasharray="5,5"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="0;-10"
                  dur="3s"
                  repeatCount="indefinite"
                />
              </path>
              <polygon
                points="65,7 70,10 65,13"
                fill="currentColor"
                className="text-primary/30"
              />
            </svg>
          </motion.div>

          {/* Subtle dot pattern for texture */}
          <div
            className="absolute inset-0 opacity-[0.08] text-primary"
            style={{
              backgroundImage: `radial-gradient(circle at 25% 25%, currentColor 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, currentColor 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
              backgroundPosition: '0 0, 25px 25px',
            }}
          ></div>

          {/* Mentorship connection lines */}
          <motion.div
            className="absolute left-[25%] top-[30%] w-60 h-60 opacity-25"
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
                className="text-blue-500"
                strokeDasharray="2,8"
              />
              <circle
                cx="50"
                cy="50"
                r="30"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
                className="text-purple-500"
                strokeDasharray="1,6"
              />
              <circle
                cx="50"
                cy="50"
                r="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
                className="text-primary"
                strokeDasharray="1,4"
              />
            </svg>
          </motion.div>

          {/* Large decorative shapes */}
          <motion.div
            className="absolute right-[10%] top-[15%] w-32 h-32 opacity-10"
            animate={{
              rotate: [0, 360],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'linear',
            }}
          >
            <div className="w-full h-full rounded-full bg-gradient-to-br from-primary/30 to-blue-500/30 blur-sm"></div>
          </motion.div>

          <motion.div
            className="absolute left-[5%] top-[40%] w-24 h-24 opacity-15"
            animate={{
              y: [0, -30, 0],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <div className="w-full h-full bg-gradient-to-tr from-purple-500/30 to-pink-500/30 rounded-2xl blur-sm"></div>
          </motion.div>

          {/* Geometric patterns */}
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern
                  id="hexagons"
                  x="0"
                  y="0"
                  width="60"
                  height="60"
                  patternUnits="userSpaceOnUse"
                >
                  <polygon
                    points="30,5 50,20 50,40 30,55 10,40 10,20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="0.5"
                    className="text-primary/20"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#hexagons)" />
            </svg>
          </div>

          {/* Floating premium particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 rounded-full bg-primary/30"
                style={{
                  left: `${15 + i * 15}%`,
                  top: `${20 + (i % 3) * 25}%`,
                }}
                animate={{
                  y: [0, -30, 0],
                  x: [0, 10, 0],
                  opacity: [0.3, 0.8, 0.3],
                  scale: [1, 1.5, 1],
                }}
                transition={{
                  duration: 4 + i * 0.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 0.8,
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <motion.div className="mb-12 text-center" {...fadeIn}>
            <h2 className="mb-4 text-3xl font-bold">Three Simple Steps</h2>
            <p className="max-w-2xl mx-auto text-muted-foreground">
              Getting started with mentorship has never been easier. Follow
              these steps to begin your journey.
            </p>
          </motion.div>

          <div className="relative">
            {/* Enhanced connection visualization for desktop */}
            <div className="absolute top-[140px] left-[16.67%] right-[16.67%] h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 opacity-30 hidden md:block rounded-full">
              <div className="absolute inset-0 rounded-full opacity-50 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 blur-sm"></div>
            </div>

            <div className="relative grid gap-8 md:grid-cols-3">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1,
                  }}
                  className="relative z-10"
                >
                  <Card className="relative h-full p-6 transition-all duration-300 bg-white border border-gray-200 hover:shadow-xl hover:-translate-y-1 group dark:bg-gray-900 dark:border-gray-800">
                    {/* Step number indicator - positioned on card */}
                    <div className="absolute z-20 -top-4 -right-4">
                      <div className="relative">
                        <div className="flex items-center justify-center w-12 h-12 border-4 border-white rounded-full shadow-lg bg-gradient-to-br from-primary to-primary/80 dark:border-gray-900">
                          <span className="text-lg font-bold text-white">
                            {index + 1}
                          </span>
                        </div>
                        <div className="absolute inset-0 w-12 h-12 rounded-full bg-primary/30 blur-lg"></div>
                      </div>
                    </div>

                    {/* Enhanced icon */}
                    <div className="flex justify-center mb-4">
                      <div
                        className={`relative inline-flex h-16 w-16 items-center justify-center rounded-xl ${step.color} text-white shadow-lg group-hover:scale-105 transition-all duration-300`}
                      >
                        <step.icon className="w-8 h-8" />
                      </div>
                    </div>

                    <div className="text-center">
                      <h3 className="mb-3 text-xl font-semibold transition-colors group-hover:text-primary">
                        {step.title}
                      </h3>
                      <p className="mb-4 text-muted-foreground">
                        {step.description}
                      </p>
                    </div>

                    <div className="space-y-2">
                      {step.details.map((detail, idx) => (
                        <div key={idx} className="flex items-start text-sm">
                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-2 mt-0.5">
                            <CheckCircle2 className="w-3 h-3 text-green-600 dark:text-green-400" />
                          </div>
                          <span className="leading-relaxed">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 bg-secondary/15">
        <div className="mx-auto max-w-7xl">
          <motion.div className="mb-12 text-center" {...fadeIn}>
            <h2 className="mb-4 text-3xl font-bold">Benefits for Everyone</h2>
            <p className="max-w-2xl mx-auto text-muted-foreground">
              Our platform is designed to create value for both mentors and
              mentees, fostering growth and connections that last.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Card className="h-full p-6">
                  <h3 className="flex items-center mb-4 text-2xl font-semibold">
                    <Users className="w-6 h-6 mr-3 text-primary" />
                    {benefit.title}
                  </h3>
                  <ul className="space-y-3">
                    {benefit.items.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <ArrowRight className="mr-2 h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <motion.div className="mb-12 text-center" {...fadeIn}>
            <h2 className="mb-4 text-3xl font-bold">
              Our Impact by the Numbers
            </h2>
            <p className="max-w-2xl mx-auto text-muted-foreground">
              See the difference we're making in the pre-health, pre-graduate,
              and research communities
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, index) => {
              const { count, setHasStarted } = useCountUp(stat.number);

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  onViewportEnter={() => setHasStarted(true)}
                  className="text-center"
                >
                  <Card className="p-6 transition-shadow hover:shadow-lg">
                    <div className="flex justify-center mb-4">
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                        <stat.icon className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                    <div className="mb-2 text-3xl font-bold text-primary">
                      {count}
                      {stat.suffix}
                    </div>
                    <p className="text-muted-foreground">{stat.label}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 bg-secondary/15">
        <div className="mx-auto max-w-7xl">
          <motion.div className="mb-12 text-center" {...fadeIn}>
            <h2 className="mb-4 text-3xl font-bold">What Our Community Says</h2>
            <p className="max-w-2xl mx-auto text-muted-foreground">
              Real stories from mentors and mentees who have found success
              through our platform
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="relative h-full p-6">
                  <Quote className="absolute w-8 h-8 top-4 right-4 text-primary/20" />

                  <div className="flex items-center mb-4">
                    <div className="flex items-center justify-center w-12 h-12 mr-4 rounded-full bg-primary/10">
                      <span className="font-semibold text-primary">
                        {testimonial.avatar}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-semibold">{testimonial.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>

                  <p className="mb-4 text-muted-foreground">
                    "{testimonial.quote}"
                  </p>

                  <div className="flex">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Sparkles
                        key={i}
                        className="w-4 h-4 text-yellow-400 fill-current"
                      />
                    ))}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div className="mb-12 text-center" {...fadeIn}>
            <h2 className="mb-4 text-3xl font-bold">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground">
              Everything you need to know about getting started with mentorship
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="overflow-hidden">
                  <button
                    className="w-full px-6 text-left transition-colors"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="pr-4 font-semibold">{faq.question}</h3>
                      {openFaq === index ? (
                        <ChevronUp className="flex-shrink-0 w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="flex-shrink-0 w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </button>

                  <motion.div
                    initial={false}
                    animate={{
                      height: openFaq === index ? 'auto' : 0,
                      opacity: openFaq === index ? 1 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </div>
                  </motion.div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="p-8 md:p-12 bg-primary/5 border-primary/20">
            <h2 className="mb-4 text-3xl font-bold">
              Ready to Start Your Journey?
            </h2>
            <p className="max-w-2xl mx-auto mb-8 text-lg text-muted-foreground">
              Join thousands of professionals, researchers, and students who are
              already benefiting from meaningful mentorship connections across
              healthcare and academia.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Button size="lg" asChild>
                <Link href="/registration/new">
                  Get Started as a Mentee
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/registration/new">
                  Become a Mentor
                  <Sparkles className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </Card>
        </motion.div>
      </section>
    </div>
  );
};

export default HowItWorks;
